import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  EnvironmentInjector,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  createComponent,
} from '@angular/core';
import { FloatLabelType } from '@angular/material/form-field';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { TableView } from './tableView';
import { TableViewAbstractComponent } from './tableViewAbstractComponent';
import { SearchFilters } from '../../../models/search/searchFilters';
import { TableViewFilters } from './tableViewFilters';
import { TableViewFiltersAbstractComponent } from './tableViewFiltersAbstractComponent';
import { Subscription } from '../../../../../../../node_modules/rxjs/dist/types';
import { Options } from './options';
import { IconType } from '../../../../../../../libs/Shared/components/icon/icon';
//import { TableViewInterfaceComponent } from './tableViewInterfaceComponent';

@Component({
  selector: 'app-filterable-table',
  templateUrl: './filterable-table.component.html',
  standalone: false,
})
export class FilterableTableComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  IconType = IconType;
  // A little bit of Angular magic here:
  // Pass in a component that implements TableViewInterfaceComponent.
  // This component will implement the UI of the data to be displayed.
  // Then, down in loadComponent(), we'll load this component into
  // the placeholder on this component.
  @Input() tableView: TableView;
  @Input() multipleTableViews: TableView[] = [];
  @Input() tableViewFilters: TableViewFilters | undefined;
  @Input() title: string = '';
  @Input() titles: string[] = [];
  @Input() toolbarButtonText: string = '';
  @Input() searchBarSize: string = '100%';
  @Input() initialPageSize: number = 10;
  @Input() multipleTableOptions: Options[] = [];
  @Input() leftToolbarButtonText: string = '';
  @Input() emptyMessage: string = 'No records matched your search.';
  @Input() hideTop: boolean = false;
  @Input() options: Options = new Options({}); // {
  // selectAction:'details' => show details (default: emit selection)
  // filterable:boolean => allow querying/filtering (default: true)
  // paginated:boolean => show pagination (default:true)
  @Input() filters: SearchFilters = new SearchFilters({});
  @Output() selectedRecord = new EventEmitter<any>();
  @Output() firstRecord = new EventEmitter<any>();
  @Output() initialized = new EventEmitter<any>();
  @Output() refreshed = new EventEmitter<number>();
  @Output() toolbarButtonClick = new EventEmitter<any>();
  @Output() leftToolbarButtonClick = new EventEmitter<any>();
  @Output() genericOutputEvent = new EventEmitter<any>();

  @ViewChild('paginator') paginator = MatPaginator;

  FILTER_TIMEOUT = 500; // Time to wait before performing a filtered query
  // Increasing this will reduce server roundtrips,
  // but also create slower filter response.
  doFilterTimeout: boolean = false;
  queryInProgress: boolean = false;
  recordCount: number = 0;
  currentPage: number = 0;

  filterable: boolean = true;
  paginated: boolean = true;

  searchString: string = ''; // input in UI
  selectedRecordType: string = '';
  pageSize: number = 10; // selectable in UI

  tableComponent: any; // this holds the instantiated table
  filterComponent: any;

  queryValueChanged: boolean = false;
  filterTimeoutId: number = 0;

  subscriptions: Subscription[] = [];

  hasBeenSearched: boolean = false;

  static htmlID: number = 0;
  myId: number = 0;
  loading: boolean = false;
  loaded: boolean = false;
  filtersVisible: boolean = false;


  selectedTableTitle: string = '';

  constructor(
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector,
    private cdr: ChangeDetectorRef,
  ) {
    this.myId = FilterableTableComponent.htmlID;
    this.tableView = TableView.prototype;
    //this.tableViewFilters = TableViewFilters.prototype;
    this.pageSize = 10;

    ++FilterableTableComponent.htmlID;
  }

  get pageStart() { return this.currentPage * this.pageSize + 1; }
  get pageEnd() { return Math.min((this.currentPage + 1) * this.pageSize, this.recordCount); }

  updatePagedData() { this.loadData(this.currentPage); }
  prevPage() { if (this.currentPage > 0) this.currentPage--; this.loadData(this.currentPage); }
  nextPage() { if (this.pageEnd < this.recordCount) this.currentPage++; this.loadData(this.currentPage); }


  ngOnInit() {
    // If this isn't done here, then an error will be thrown
    /*
    if (this.options.filterable !== null && this.options.filterable == false) {
      this.filterable = false;
    }
    if (this.options.paginated && this.options.paginated == false) {
      this.paginated = false;
    }
*/
    this.pageSize = this.initialPageSize;
  }

  ngOnDestroy() {
    for (let i = this.subscriptions.length - 1; i >= 0; i--) {
      this.subscriptions[i].unsubscribe();
      this.subscriptions.pop();
    }
    this.destroyDynamicComponents();
  }

  ngAfterViewInit() {
    if (this.multipleTableViews.length > 0) {
      this.selectedTableTitle = this.titles[0];
      this.tableView = this.multipleTableViews[0];
      this.options = this.multipleTableOptions[0];
    }
    this._loadComponent(true);
  }

  public reload(pageNum: number | void): void {
    // Unless a specific page is specified, current page won't change

    let pageToUse = this.currentPage;

    if (pageNum && pageNum >= 0) {
      pageToUse = pageNum;
    }

    this.loadData(pageToUse);
  }

  selectTableTitle(title: string) {
    this.selectedTableTitle = title;

    for (let i = this.subscriptions.length - 1; i >= 0; i--) {
      this.subscriptions[i].unsubscribe();
      this.subscriptions.pop();
    }
    this.destroyDynamicComponents();

    const index = this.titles.indexOf(title);
    this.tableView = this.multipleTableViews[index];
    this.options = this.multipleTableOptions[index];
    this.loaded = false;
    this.recordCount = 0;
    this.currentPage = 0;

    // Host nodes are removed on destroy; recreate the stable containers first.
    this.cdr.detectChanges();
    this.loadComponent();
    // Applies invoiceStatus/etc. to the new instance and triggers its initial query.
    this.setOptions(this.options);
  }

  private destroyDynamicComponents() {
    if (this.tableComponent) {
      this.appRef.detachView(this.tableComponent.hostView);
      this.tableComponent.destroy();
      this.tableComponent = undefined;
    }
    if (this.filterComponent) {
      this.appRef.detachView(this.filterComponent.hostView);
      this.filterComponent.destroy();
      this.filterComponent = undefined;
    }
  }

  /** createComponent destroys its hostElement on destroy — never use the template node as host. */
  private createHostElement(containerId: string): HTMLElement | undefined{
    const container = document.getElementById(containerId);
    if (!container) {
      //throw new Error(`Missing container #${containerId}`);
      return undefined;
    }
    container.replaceChildren();
    const host = document.createElement('div');
    container.appendChild(host);
    return host;
  }

  toolbarButtonClicked() {
    this.toolbarButtonClick.emit();
  }

  leftToolbarButtonClicked() {
    this.leftToolbarButtonClick.emit();
  }

  loadComponent() {
    this._loadComponent(false);
  }

  _loadComponent(afterViewInit: boolean) {
    if (!this.loaded && this.tableView) {
      if (!this.options.pageSize) {
        this.options.pageSize = this.pageSize;
      }

      this.tableComponent = createComponent<TableViewAbstractComponent>(
        this.tableView.component,
        {
          environmentInjector: this.environmentInjector,
          hostElement: this.createHostElement('table-container' + this.myId),
        },
      );

      this.appRef.attachView(this.tableComponent.hostView);
      this.tableComponent.instance.recordSelectAction = 'return'; //TODO: add this to options{}
      this.subscriptions.push(
        this.tableComponent.instance.genericOutputEvent.subscribe((value: any) => {
          this.genericOutputEvent.emit(value);
        })
      )
      this.subscriptions.push(
        this.tableComponent.instance.selectedRecord.subscribe((value: any) =>
          this.handleSelectedRecord(value),
        ),
      );
      this.subscriptions.push(
        this.tableComponent.instance.recordSetUpdated.subscribe(
          (value: any) => {
            this.recordCount = value.recordCount;
            this.refreshed.emit(value);
          },
        ),
      );
      this.subscriptions.push(
        this.tableComponent.instance.recordSetUpdating.subscribe(
          (value: boolean) => {
            this.loading = value;
            this.cdr.detectChanges(); // Not doing this will trigger a ExpressionChangedAfterItHasBeenCheckedError
          },
        ),
      );

      // add filters here
      if (this.tableViewFilters) {
        this.filterComponent =
          createComponent<TableViewFiltersAbstractComponent>(
            this.tableViewFilters.component,
            {
              environmentInjector: this.environmentInjector,
              hostElement: this.createHostElement(
                'filter-container' + this.myId,
              ),
            },
          );
        this.appRef.attachView(this.filterComponent.hostView);
      }

      this.loaded = true;
      this.initialized.emit(true);
    }
  }

  setOptions(options: Options) {
    this.options = options;

    if (this.options.filterable !== null && this.options.filterable == false) {
      this.filterable = false;
    }

    if (
      this.pageSize != 5 &&
      this.pageSize != 10 &&
      this.pageSize != 25 &&
      this.pageSize != 50
    ) {
      this.pageSize = 5;
    }

    this.cdr.detectChanges();
    this.tableComponent.instance.setOptions(this.options);
  }

  clearSelection() {
    this.tableComponent.instance.clearSelection();
  }

  handleSelectedRecord(record: any) {
    this.selectedRecord.emit(record);
  }

  public handleDataPageEvent(event?: PageEvent) {
    if (event) {
      if (event.pageSize != this.pageSize) {
        this.pageSize = event.pageSize;
        this.currentPage = 0;
        this.loadData(this.currentPage);
      } else {
        this.loadData(event?.pageIndex);
      }
    }

    return event;
  }

  btnShowFilters_click() {
    this.filtersVisible = true;
  }

  btnApplyFilters_click() {
    this.loadData(0);
  }

  btnHideFilters_click() {
    this.filtersVisible = false;
    this.loadData(0);
  }

  async loadData(pageNum: number) {
    this.hasBeenSearched = true;

    let filterOptions: SearchFilters | undefined = undefined;

    if (this.filtersVisible) {
      filterOptions = this.filterComponent.instance.getFilters();
    } else {
      filterOptions = new SearchFilters({});

      switch (this.selectedRecordType) {
        case 'customers':
          filterOptions.returnCustomers = true;
          break;
        case 'merchants':
          filterOptions.returnMerchants = true;
          break;
        case 'transactions':
          filterOptions.returnTransactions = true;
          break;
        case 'users':
          filterOptions.returnUsers = true;
          break;
        default:
          filterOptions.returnCustomers = true;
          filterOptions.returnMerchants = true;
          filterOptions.returnBusinesses = true;
          filterOptions.returnUsers = true;
          filterOptions.returnTransactions = true;
      }
    }
    let that = this;

    let pageToUse = this.currentPage;

    if (pageNum >= 0) {
      pageToUse = pageNum;
    }

    if (!this.queryInProgress) {
      this.queryInProgress = true;
    }

    return this.tableComponent.instance
      .reload(this.searchString, filterOptions, pageToUse, this.pageSize)
      .then((queryCompleted: boolean) => {
        // true means that the query happened
        // false means it didn't because a query was already active
        that.queryInProgress = !queryCompleted;
        that.doFilterTimeout = false;
        this.currentPage = pageNum;
      });
  }

  ddlRecordType_changed() {
    if (this.searchString.trim() != '') {
      this.loadData(0);
    }
  }

  txtFilter_keyup(event: any) { // fixing immediate typing issue
    this.queryValueChanged = true;
  
    if (this.filterTimeoutId) {
      window.clearTimeout(this.filterTimeoutId);
    }
  
    this.filterTimeoutId = window.setTimeout(() => {
      this.filterTimeoutId = 0;
  
      if (this.queryValueChanged && !this.queryInProgress) {
        this._handleTheQuery();
        this.queryValueChanged = false;
      }
    }, this.FILTER_TIMEOUT);
  }

  // This function gets called recursively if the query text changed since the last
  // query was fired off. Otherwise, it's fires on keyup.
  _handleTheQuery() {
    let that = this;

    that.queryInProgress = true;

    that.loadData(0).then(() => {
      that.queryInProgress = false;
      if (that.queryValueChanged) {
        that.queryValueChanged = false;
        that._handleTheQuery();
      }
    });
  }

  getFloatLabelValue(): FloatLabelType {
    return 'auto';
  }
}
